import userFiltersSignal, {
	setUserFilters,
} from '@UI/Pages/Administration/Users/Filters/Signals/userFiltersSignal';
import type { UserSortField } from '@UI/Pages/Administration/Users/Filters/UserFilterView';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { Dropdown } from '@verclam/ui-kit';

type SortOption = {
	readonly value: string;
	readonly label: string;
	readonly sortField: UserSortField;
	readonly sortOrder: 1 | -1;
};

/** The two fields the server knows how to sort by (`lastName`, `email`), each direction spelled out
 * as its own option. */
const SORT_OPTIONS: readonly SortOption[] = [
	{ value: 'lastName-asc', label: 'Nom (A→Z)', sortField: 'lastName', sortOrder: 1 },
	{ value: 'lastName-desc', label: 'Nom (Z→A)', sortField: 'lastName', sortOrder: -1 },
	{ value: 'email-asc', label: 'Courriel (A→Z)', sortField: 'email', sortOrder: 1 },
	{ value: 'email-desc', label: 'Courriel (Z→A)', sortField: 'email', sortOrder: -1 },
];

type Props = {
	readonly withLabel?: boolean;
};

const SortFilter: React.FC<Props> = ({ withLabel = false }) => {
	const currentFilters = useSignalValue(userFiltersSignal);

	const handleSortChange = React.useCallback((value: string | null) => {
		const option = SORT_OPTIONS.find((candidate) => candidate.value === value);
		setUserFilters(
			userFiltersSignal.peek().setSort(option?.sortField ?? null, option?.sortOrder ?? null),
		);
	}, []);

	const currentSortValue = React.useMemo(() => {
		if (currentFilters.sortField === null || currentFilters.sortOrder === null) {
			return null;
		}

		return (
			SORT_OPTIONS.find(
				(option) =>
					option.sortField === currentFilters.sortField &&
					option.sortOrder === currentFilters.sortOrder,
			)?.value ?? null
		);
	}, [currentFilters.sortField, currentFilters.sortOrder]);

	return (
		<Dropdown<string>
			name="userSort"
			label="Trier par"
			isLabelHidden={!withLabel}
			placeholder="Tri par défaut"
			options={SORT_OPTIONS}
			optionLabel="label"
			optionValue="value"
			showClear
			value={currentSortValue}
			onChange={handleSortChange}
		/>
	);
};

export default SortFilter;
